import { useSyncExternalStore } from 'react';
import { env, REACT_APP_SERVER_API } from 'src/config';
import { getHeaders } from 'src/common';
import { prepareAISearchImage } from './prepareAISearchImage';

let activeController = null;

// Read-only search: smaller uploads and independent catalogue/visual results.
let serial = 0;
let state = { id: 0, active: false, busy: false, kind: '', query: '', preview: '', sourceFile: null, results: [], indexed: 0, complete: false, error: '' };
const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());
function update(next) { state = { ...state, ...next }; notify(); }
export const getAISnapshot = () => state;
export const useAISearch = () => useSyncExternalStore(fn => { listeners.add(fn); return () => listeners.delete(fn); }, () => state);
export function clearAISearch() {
  ++serial;
  activeController?.abort();
  activeController = null;
  if (state.preview) URL.revokeObjectURL(state.preview);
  state = { id: serial, active: false, busy: false, kind: '', query: '', preview: '', sourceFile: null, results: [], indexed: 0, complete: false, error: '' };
  notify();
}
export function toggleAIBookmark(modelId, bookmarked) {
  update({ results: state.results.map(m => m.id === modelId ? { ...m, bookmarked } : m) });
}

async function parseJson(response, fallback) {
  if (!response.headers.get('content-type')?.includes('json')) return { error: fallback };
  try { return await response.json(); } catch (_) { return { error: fallback }; }
}

function localize(model, lang) {
  if (!model) return null;
  const translated = model.translate?.find(t => t.lang === ({ az: 'AZE', ru: 'RUS', en: 'ENG' }[lang] || 'ENG'));
  return { ...model, title: translated?.title || model.title };
}

async function imageMatches(kind, value, language, signal) {
  const response = await fetch('/image-ai/search/' + kind, {
    method: 'POST',
    headers: kind === 'text' ? { 'Content-Type': 'application/json' } : { 'Content-Type': value.type || 'application/octet-stream' },
    body: kind === 'text' ? JSON.stringify({ query: value, lang: language }) : value,
    signal,
  });
  const ai = await parseJson(response, `AI HTTP ${response.status}`);
  if (!response.ok) throw new Error(ai.error || `AI HTTP ${response.status}`);
  const hits = Array.isArray(ai.results) ? ai.results : [];
  const names = [...new Set(hits.map(hit => hit.name).filter(name => typeof name === 'string'))].slice(0, 200);
  const matched = [];
  if (names.length) {
    const result = await fetch(env(REACT_APP_SERVER_API) + 'ai/resolve-images', {
      method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ names, compact: true }), signal,
    });
    const data = await parseJson(result, `Model API HTTP ${result.status}`);
    if (!result.ok) throw new Error(data.code || data.error || `Model API HTTP ${result.status}`);
    const cards = new Map((data.models || []).map(model => [model.id, model]));
    const lookup = new Map((data.result || []).map(hit => [hit.name, hit.model || cards.get(hit.model_id)]));
    const seen = new Set();
    for (const hit of hits) {
      const model = lookup.get(hit.name);
      if (!model?.slug || seen.has(model.id)) continue;
      seen.add(model.id);
      const images = Array.isArray(model.images) ? model.images : [];
      const matchedImage = images.find(img => img.path === hit.name);
      matched.push({ ...localize(model, language),
        images: matchedImage ? [matchedImage, ...images.filter(img => img !== matchedImage)] : images,
        _aiScore: Number(hit.score) || 0, _aiImage: hit.name });
    }
  }
  return { models: matched, indexed: ai.indexed_images || 0, complete: !!ai.index_complete };
}

async function catalogueMatches(query, language, signal) {
  const response = await fetch(env(REACT_APP_SERVER_API) + 'ai/search-catalog', {
    method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }), signal,
  });
  const data = await parseJson(response, `Model API HTTP ${response.status}`);
  if (!response.ok) throw new Error(data.code || data.error || `Model API HTTP ${response.status}`);
  return { models: (Array.isArray(data.result) ? data.result : [])
    .filter(hit => hit?.model?.slug)
    .map(hit => ({ ...localize(hit.model, language), _catalogScore: Number(hit.score) || 0,
      _catalogCoverage: Number(hit.coverage) || 0, _catalogGroups: Number(hit.group_count) || 0,
      _catalogFullMatch: !!hit.full_match })),
    semanticQuery: typeof data.semantic_query === 'string' ? data.semantic_query : null,
    truncated: !!data.truncated };
}

// A transparent fusion: matching titles/tags rank ahead of vague visual hits;
// results are deduplicated by the actual OneClick model id, never a guessed slug.
function combineText(catalogue, visual) {
  const combined = new Map();
  // Visual similarity is a useful fallback when catalogue metadata is absent,
  // but cannot outrank a model whose real title/tags satisfy all text concepts.
  for (const item of visual) combined.set(item.id, { ...item,
    _matchTier: 0, _textRank: Math.max(0, item._aiScore || 0) });
  for (const item of catalogue) {
    const previous = combined.get(item.id);
    const visualScore = Math.max(0, previous?._aiScore || 0);
    const coverage = item._catalogGroups > 0 ? item._catalogCoverage / item._catalogGroups : 0;
    const full = item._catalogFullMatch || (item._catalogGroups > 0 && coverage === 1);
    combined.set(item.id, { ...previous, ...item,
      images: previous?._aiImage ? previous.images : item.images,
      _aiImage: previous?._aiImage || '', _aiScore: previous?._aiScore || 0,
      _matchTier: full ? 2 : 1,
      _textRank: Math.max(0, item._catalogScore || 0) * 0.82 + visualScore * 0.18 });
  }
  return [...combined.values()].sort((a,b) => b._matchTier - a._matchTier || b._textRank - a._textRank).slice(0, 120);
}

async function planSemanticQuery(query, signal) {
  const response = await fetch(env(REACT_APP_SERVER_API) + 'ai/plan-query', {
    method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }), signal,
  });
  const data = await parseJson(response, `Model API HTTP ${response.status}`);
  if (!response.ok) throw new Error(data.code || `Model API HTTP ${response.status}`);
  return typeof data.semantic_query === 'string' ? data.semantic_query : null;
}

export async function runAISearch(kind, value, language = 'en') {
  const id = ++serial;
  activeController?.abort();
  const abort = new AbortController();
  activeController = abort;
  const preview = kind === 'image' ? URL.createObjectURL(value) : '';
  const previousPreview = state.preview;
  update({ id, active: true, busy: true, kind, query: kind === 'text' ? value : '', preview, sourceFile: kind === 'image' ? value : null, results: [], indexed: 0, complete: false, error: '' });
  if (previousPreview) URL.revokeObjectURL(previousPreview);
  const timeout = setTimeout(() => abort.abort(), 120000);
  try {
    if (kind === 'image') {
      const upload = await prepareAISearchImage(value, abort.signal);
      if (abort.signal.aborted) throw new DOMException('Search cancelled', 'AbortError');
      const ai = await imageMatches('image', upload, language, abort.signal);
      if (id === serial) update({ busy: false, results: ai.models, indexed: ai.indexed, complete: ai.complete });
      return;
    }
    let catalogue = null, ai = null, catalogError = null, aiError = null;
    const publishPartial = () => {
      if (id !== serial || abort.signal.aborted) return;
      const results = combineText(catalogue?.models || [], ai?.models || []);
      if (results.length) update({ results, busy: true, error: '',
        indexed: ai?.indexed || 0, complete: ai?.complete || false });
    };
    const catalogTask = catalogueMatches(value, language, abort.signal)
      .then(data => { catalogue = data; publishPartial(); return data; })
      .catch(error => { catalogError = error; return null; });
    // The same backend planner serves both endpoints. No second dictionary,
    // speculative translation, or wait for the catalogue SQL query is needed.
    // If the new route is unavailable, the old catalogue response is the fallback.
    const planTask = planSemanticQuery(value, abort.signal)
      .catch(() => catalogTask.then(data => data?.semanticQuery || null));
    const semantic = await Promise.race([
      planTask,
      catalogTask.then(data => data ? data.semanticQuery : planTask),
    ]);
    if (semantic && !abort.signal.aborted) {
      try {
        ai = await imageMatches('text', semantic, language, abort.signal);
        publishPartial();
      } catch (error) { aiError = error; }
    }
    await catalogTask;
    if (abort.signal.aborted) throw new DOMException('Search cancelled', 'AbortError');
    if (!catalogue && !ai) throw catalogError || aiError || new Error('AI_TEXT_SEARCH_UNAVAILABLE');
    const results = combineText(catalogue?.models || [], ai?.models || []);
    if (id === serial) update({ busy: false, results, indexed: ai?.indexed || 0, complete: ai?.complete || false,
      error: results.length ? '' : (catalogError?.message || aiError?.message || '') });
  } catch (error) {
    if (id === serial) update({ busy: false, error: error.name === 'AbortError' ? 'Axtarış vaxtı bitdi.' : String(error.message || 'AI axtarışı alınmadı.') });
  } finally {
    clearTimeout(timeout);
    abort.abort(); // Cancel a redundant planner request if catalogue finished first.
    if (activeController === abort) activeController = null;
  }
}
